/*
 * Contador de chamados do menu, compartilhado entre o menu e a tela.
 *
 * Um intervalo so para o painel inteiro: o menu fica sempre montado, e a tela
 * de chamados pede uma atualizacao na hora quando muda algo — assim o numero
 * do menu nao fica um minuto mentindo depois de uma resposta.
 */
import { useEffect, useRef, useState } from 'react';
import { apiRequest } from './api';
import type { TicketCountersDto } from './types';

const INTERVALO_MS = 60_000;
const EVENTO = 'hotspot:chamados-mudaram';

/** Conversa aberta: a outra pessoa pode estar respondendo agora. */
export const INTERVALO_CONVERSA_MS = 10_000;
export const INTERVALO_LISTA_MS = 30_000;

/**
 * Repete `acao` a cada `intervaloMs`, mas so com a aba visivel.
 *
 * Painel esquecido aberto numa aba de fundo nao deve consultar o servidor o
 * dia inteiro. Ao voltar para a aba, consulta na hora — e o momento em que a
 * pessoa vai olhar, e esperar o proximo ciclo mostraria dado velho.
 *
 * Nao faz a primeira carga: quem chama ja carregou ao montar.
 */
export function useAtualizacaoPeriodica(acao: () => void, intervaloMs: number): void {
  const ultima = useRef(acao);
  useEffect(() => {
    ultima.current = acao;
  });

  useEffect(() => {
    let timer: number | undefined;
    const parar = () => {
      window.clearInterval(timer);
      timer = undefined;
    };
    const iniciar = () => {
      parar();
      timer = window.setInterval(() => ultima.current(), intervaloMs);
    };
    const aoMudarDeAba = () => {
      if (document.hidden) {
        parar();
      } else {
        ultima.current();
        iniciar();
      }
    };

    if (!document.hidden) iniciar();
    document.addEventListener('visibilitychange', aoMudarDeAba);
    return () => {
      parar();
      document.removeEventListener('visibilitychange', aoMudarDeAba);
    };
  }, [intervaloMs]);
}

/** Avise o menu de que algo mudou (resposta enviada, chamado lido). */
export function avisarMudancaNosChamados(): void {
  window.dispatchEvent(new CustomEvent(EVENTO));
}

export function useContadoresDeChamados(): TicketCountersDto | null {
  const [contadores, setContadores] = useState<TicketCountersDto | null>(null);

  const carregar = () =>
    apiRequest<TicketCountersDto>('/admin/tickets/counters')
      .then(setContadores)
      // Falha no contador nao e motivo para incomodar ninguem.
      .catch(() => {});

  useEffect(() => {
    void carregar();
    window.addEventListener(EVENTO, carregar);
    return () => window.removeEventListener(EVENTO, carregar);
  }, []);

  useAtualizacaoPeriodica(carregar, INTERVALO_MS);

  return contadores;
}
